export const DIFFICULTY_LEVELS = [
  {
    id: 'junior',
    label: 'Junior',
    color: '#2e7d32',
  },
  {
    id: 'middle',
    label: 'Middle',
    color: '#e65100',
  },
  {
    id: 'senior',
    label: 'Senior',
    color: '#1565c0',
  },
];

export const DEFAULT_DIFFICULTY = 'junior';

const patternRecognitionDifficulty = {
  junior: {
    tagline: 'Явные признаки, очевидный ответ',
    bullets: [
      'Один паттерн, короткий чистый код',
      'Варианты из одной категории',
      'Ответ очевиден для подготовленного Junior',
    ],
  },
  middle: {
    tagline: 'Реалистичный код, похожие паттерны',
    bullets: [
      'Умеренный шум, реалистичный код',
      'Дистракторы — правдоподобные и путаемые',
      'Фокус на различении схожих решений',
    ],
  },
  senior: {
    tagline: 'Пограничные случаи, неоднозначность',
    bullets: [
      'Высокий шум, возможен второй паттерн',
      'Пограничные случаи и неоднозначность',
      'Требует глубокого понимания архитектуры',
    ],
  },
};

const problemRecognitionDifficulty = {
  junior: {
    tagline: 'Явная проблема, прямое решение',
    bullets: [
      'Проблема видна по короткому фрагменту кода',
      'Подходящий паттерн решает основную боль напрямую',
      'Дистракторы заметно хуже по задаче',
    ],
  },
  middle: {
    tagline: 'Практический контекст, близкие решения',
    bullets: [
      'Проблема проявляется через несколько ограничений',
      'Нужно отличить похожие архитектурные решения',
      'Варианты ответа выглядят правдоподобно',
    ],
  },
  senior: {
    tagline: 'Компромиссы, ограничения, побочные эффекты',
    bullets: [
      'Проблема зависит от контекста и будущих изменений',
      'Есть несколько допустимых направлений решения',
      'Нужно выбрать паттерн с лучшим набором компромиссов',
    ],
  },
};

const patternHuntDifficulty = {
  junior: {
    tagline: 'Одна заметная проблема',
    bullets: [
      'Нарушение дизайна видно по основному фрагменту',
      'Достаточно найти один ключевой паттерн',
      'Решение проверяет базовое понимание роли паттерна',
    ],
  },
  middle: {
    tagline: 'Несколько связанных запахов кода',
    bullets: [
      'Проблемы распределены по нескольким классам',
      'Нужно отделить симптом от причины',
      'Решение может включать комбинацию подходов',
    ],
  },
  senior: {
    tagline: 'Системный разбор дизайна',
    bullets: [
      'Нужно увидеть несколько архитектурных рисков',
      'Паттерны конкурируют с более простыми изменениями',
      'Оценка зависит от границ ответственности',
    ],
  },
};

const sandboxDifficulty = {
  junior: {
    tagline: 'Небольшая реализация',
    bullets: [
      'Задача с коротким и понятным контрактом',
      'Достаточно базовой структуры паттерна',
      'Проверяются основные роли участников',
    ],
  },
  middle: {
    tagline: 'Реалистичная задача с ограничениями',
    bullets: [
      'Нужно учесть несколько сценариев использования',
      'Решение должно оставаться расширяемым',
      'Проверяется качество границ и именования',
    ],
  },
  senior: {
    tagline: 'Открытая задача с компромиссами',
    bullets: [
      'Требуется объяснимое архитектурное решение',
      'Есть альтернативы с разными последствиями',
      'Проверяется устойчивость дизайна к изменениям',
    ],
  },
};

export const PROGRAMMING_LANGUAGES = [
  { value: 'Python', label: 'Python' },
  { value: 'JavaScript', label: 'JavaScript' },
  { value: 'Java', label: 'Java' },
  { value: 'C++', label: 'C++' },
  { value: 'C#', label: 'C#' },
  { value: 'TypeScript', label: 'TypeScript' },
  { value: 'PHP', label: 'PHP' },
  { value: 'Go', label: 'Go' },
  { value: 'Rust', label: 'Rust' },
];

export const TRAINERS = [
  {
    id: 'pattern-recognition',
    title: 'Распознавание паттернов',
    subtitle: 'Pattern Recognition',
    description: 'Видите код -- определяете, какой паттерн реализован.',
    path: '/trainer/pattern-recognition',
    active: true,
    difficultyDescriptions: patternRecognitionDifficulty,
  },
  {
    id: 'problem-recognition',
    title: 'Распознавание проблем',
    subtitle: 'Problem Recognition',
    description: 'Видите проблему в коде -- определяете, какой паттерн подойдёт лучше всего.',
    path: '/trainer/problem-recognition',
    active: true,
    difficultyDescriptions: problemRecognitionDifficulty,
  },
  {
    id: 'pattern-hunt',
    title: 'Охота за паттернами',
    subtitle: 'Pattern Hunt',
    description: 'Код с нарушениями SOLID -- находите, какие паттерны решают проблемы.',
    path: '/trainer/pattern-hunt',
    active: false,
    difficultyDescriptions: patternHuntDifficulty,
  },
  {
    id: 'sandbox',
    title: 'Песочница',
    subtitle: 'Sandbox',
    description: 'Бизнес-задача -- пишете реализацию на выбранном языке, AI проверяет результат.',
    path: '/trainer/sandbox',
    active: false,
    difficultyDescriptions: sandboxDifficulty,
  },
];

export function getDifficultyLevelsForTrainer(trainer) {
  return DIFFICULTY_LEVELS.map((level) => ({
    ...level,
    ...(trainer?.difficultyDescriptions?.[level.id] || {}),
  }));
}

export function getLanguageDisplayName(value) {
  const lang = PROGRAMMING_LANGUAGES.find(l => l.value === value);
  return lang ? lang.label : value;
}
